import type { BoxProps } from "@chakra-ui/react"
import { KEYED_DP_SEPARATOR, type Kpi, type RatingDatapoints } from "@esgt/types"
import { MethodI18nFragment } from "components/I18nText"
import { formatKpiValue, getKpiUnitLabel } from "lib/helpers/formatKpiValue"
import type React from "react"
import { getBreakdownRows } from "../../Ratings/Rating/KPI/CategoryKPIs/getBreakdownRows"
import { ReportTable, type ReportTableColumn } from "./ReportTable"

interface BreakdownKpiTableProps extends BoxProps {
  kpi: Kpi
  datapoints: RatingDatapoints
  answerValues?: Record<string, Record<string, string> | undefined>
}

/**
 * Converts a breakdown KPI's segment/prefix config and datapoints into
 * the generic `ReportTable` column/row format.
 *
 * Units are resolved in two ways (in priority order):
 *
 * 1. **Per-row from `isUnit` segments** – when a suffix segment is marked
 *    with `isUnit: true`, it is removed from the visible columns and its
 *    resolved label is appended to every value cell in the row instead
 *    (e.g. "16 kg" instead of a separate "kg" column).
 *
 * 2. **KPI-level from `kpi.unit`** – when no `isUnit` segments exist but
 *    the KPI has a unit (including `fromQuestion`), that unit is used for
 *    all rows.  This enforces a single unit across the whole table while
 *    still allowing dynamic resolution from a question answer.
 *
 * Extra `BoxProps` (e.g. `data-breakdown`) are forwarded to the root element.
 */
export const BreakdownKpiTable: React.FC<BreakdownKpiTableProps> = ({ kpi, datapoints, answerValues, ...rest }) => {
  if (!kpi.breakdown) return null

  const { suffixSegments, valuePrefixes } = kpi.breakdown
  const breakdownRows = getBreakdownRows(datapoints, kpi.breakdown)

  // null means no data and no fallback — skip rendering entirely.
  if (!breakdownRows) return null

  // Partition segments into unit segments (hidden from columns, label appended
  // to values) and display segments (rendered as regular columns).
  const unitSegmentIndices: number[] = []
  const displaySegmentIndices: number[] = []
  for (let i = 0; i < suffixSegments.length; i++) {
    if (suffixSegments[i].isUnit) {
      unitSegmentIndices.push(i)
    } else {
      displaySegmentIndices.push(i)
    }
  }

  // When no isUnit segments exist, fall back to the KPI-level unit.
  const kpiUnitLabel = unitSegmentIndices.length === 0 ? getKpiUnitLabel(kpi.unit, answerValues) : ""

  const columns: ReportTableColumn[] = [
    ...displaySegmentIndices.map((idx) => ({
      header: <MethodI18nFragment text={suffixSegments[idx].header} />,
      align: "left" as const,
    })),
    ...valuePrefixes.map((vp) => ({
      header: <MethodI18nFragment text={vp.header} />,
      align: "right" as const,
    })),
  ]

  const rows = breakdownRows.map((row) => {
    // For isUnit segments, extract the raw code from the key suffix
    // and resolve through the unit registry for a compact label.
    const keyParts = row.key.split(KEYED_DP_SEPARATOR)
    const rowUnitCode = unitSegmentIndices.map((idx) => keyParts[idx] ?? "").find(Boolean) ?? ""
    const unitLabel = rowUnitCode ? getKpiUnitLabel(rowUnitCode) : kpiUnitLabel

    // Choose the unit for formatting: per-row code when available,
    // otherwise the KPI-level unit (which may be a fromQuestion object).
    const formatUnit = rowUnitCode || kpi.unit

    const displayColumns = displaySegmentIndices.map((idx): React.ReactNode => row.columns[idx])

    const displayValues = row.values.map((val): React.ReactNode => {
      const formatted = formatKpiValue(val, formatUnit, answerValues)
      if (unitLabel) {
        return `${formatted} ${unitLabel}`
      }
      return formatted || "-"
    })

    return [...displayColumns, ...displayValues]
  })

  return <ReportTable columns={columns} rows={rows} emptyMessage="Ingen data registrert" {...rest} />
}
